import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { CITY_CENTERS, cityCenter, jitterAround, neighborhoodList } from "@/lib/locations";
import PhotoPicker from "./PhotoPicker";
import FeaturePicker from "./FeaturePicker";

const CATEGORIES = ["SUV", "Sedan", "Van", "Hatchback", "Wagon"];
const TRANSMISSIONS = ["Automatic", "Manual"];
const FUEL_TYPES = ["Petrol", "Diesel", "Hybrid", "Electric"];

const inputClass =
  "w-full rounded-lg border border-background-200/70 bg-background-50 px-3 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:border-primary-500 focus:outline-none";
const labelClass = "mb-2 block text-xs font-medium uppercase tracking-wider text-foreground-500";

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl surface-card p-5 md:p-7">
      <h2 className="font-heading text-lg font-semibold text-foreground-950">{title}</h2>
      <p className="mt-1 text-sm text-foreground-600">{subtitle}</p>
      <div className="mt-5">{children}</div>
    </section>
  );
}

export default function ListingForm() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [category, setCategory] = useState("SUV");
  const [transmission, setTransmission] = useState("Automatic");
  const [fuelType, setFuelType] = useState("Petrol");
  const [seats, setSeats] = useState("5");
  const [doors, setDoors] = useState("5");
  const [price, setPrice] = useState("");
  const [city, setCity] = useState("Nairobi");
  const [location, setLocation] = useState(neighborhoodList("Nairobi")[0] || "");
  const [images, setImages] = useState<string[]>([]);
  const [features, setFeatures] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [instantBook, setInstantBook] = useState(true);
  const [delivery, setDelivery] = useState(false);

  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [formError, setFormError] = useState("");

  function handleCityChange(next: string) {
    setCity(next);
    setLocation(neighborhoodList(next)[0] || "");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!user) return;

    setFormError("");
    if (make.trim().length < 2 || model.trim().length < 1) {
      setStatus("error");
      setFormError("Please add the car's make and model.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setStatus("error");
      setFormError("Set a daily price so riders know what to expect.");
      return;
    }
    if (images.length === 0) {
      setStatus("error");
      setFormError("Add at least one photo of your car.");
      return;
    }

    setStatus("loading");
    try {
      const center = cityCenter(city);
      const seed = Number(String(Date.now()).slice(-6)) / 1000;
      const point = jitterAround(center.lat, center.lng, seed);
      const fullLocation = location ? `${location}, ${city}` : city;

      const { data, error } = await supabase
        .from("vehicles")
        .insert({
          owner_id: user.id,
          owner_name: profile?.full_name || user.email || "Gari host",
          make: make.trim(),
          model: model.trim(),
          year: year ? Number(year) : null,
          category,
          body_type: category,
          transmission,
          fuel_type: fuelType,
          seats: seats ? Number(seats) : null,
          doors: doors ? Number(doors) : null,
          price_per_day: Number(price),
          location: fullLocation,
          city,
          latitude: Number(point.lat.toFixed(6)),
          longitude: Number(point.lng.toFixed(6)),
          description: description.trim() || null,
          images: images.map((url) => ({ url, type: "image" })),
          features,
          rating: 0,
          reviews_count: 0,
          trips_count: 0,
          available: true,
          instant_book: instantBook,
          delivery,
          status: "active",
        })
        .select("id")
        .single();

      if (error) throw error;

      if (profile && profile.role !== "host") {
        await supabase.from("profiles").update({ role: "host", is_driver: true }).eq("id", user.id);
        await refreshProfile();
      }

      navigate(`/vehicle/${data.id}`);
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "We couldn't publish your car. Please try again.");
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Section title="The car" subtitle="Basic details riders will see first.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="car-make" className={labelClass}>Make</label>
            <input id="car-make" value={make} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Toyota" className={inputClass} required />
          </div>
          <div>
            <label htmlFor="car-model" className={labelClass}>Model</label>
            <input id="car-model" value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Land Cruiser Prado" className={inputClass} required />
          </div>
          <div>
            <label htmlFor="car-year" className={labelClass}>Year</label>
            <input id="car-year" type="number" min={1990} max={2030} value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2019" className={inputClass} />
          </div>
          <div>
            <label htmlFor="car-category" className={labelClass}>Category</label>
            <select id="car-category" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputClass} cursor-pointer`}>
              {CATEGORIES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      <Section title="Specs" subtitle="Help riders pick the right car for the trip.">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <label htmlFor="car-transmission" className={labelClass}>Transmission</label>
            <select id="car-transmission" value={transmission} onChange={(e) => setTransmission(e.target.value)} className={`${inputClass} cursor-pointer`}>
              {TRANSMISSIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="car-fuel" className={labelClass}>Fuel</label>
            <select id="car-fuel" value={fuelType} onChange={(e) => setFuelType(e.target.value)} className={`${inputClass} cursor-pointer`}>
              {FUEL_TYPES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="car-seats" className={labelClass}>Seats</label>
            <input id="car-seats" type="number" min={1} max={20} value={seats} onChange={(e) => setSeats(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="car-doors" className={labelClass}>Doors</label>
            <input id="car-doors" type="number" min={1} max={6} value={doors} onChange={(e) => setDoors(e.target.value)} className={inputClass} />
          </div>
        </div>
      </Section>

      <Section title="Pricing & location" subtitle="Where the car lives and what it costs per day.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="car-price" className={labelClass}>Price per day (KES)</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">KSh</span>
              <input id="car-price" type="number" min={0} step={100} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="8000" className={`${inputClass} pl-11`} required />
            </div>
          </div>
          <div>
            <label htmlFor="car-city" className={labelClass}>City</label>
            <select id="car-city" value={city} onChange={(e) => handleCityChange(e.target.value)} className={`${inputClass} cursor-pointer`}>
              {CITY_CENTERS.map((entry) => (
                <option key={entry.city} value={entry.city}>{entry.city}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="car-location" className={labelClass}>Neighbourhood</label>
            <select id="car-location" value={location} onChange={(e) => setLocation(e.target.value)} className={`${inputClass} cursor-pointer`}>
              {neighborhoodList(city).map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      <Section title="Photos" subtitle="Great photos get booked up to 3x faster.">
        <PhotoPicker images={images} onChange={setImages} />
      </Section>

      <Section title="What's included" subtitle="Tap everything your car offers.">
        <FeaturePicker selected={features} onChange={setFeatures} />
      </Section>

      <Section title="Description" subtitle="Tell riders what makes your car great.">
        <textarea
          id="car-description"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 500))}
          rows={4}
          maxLength={500}
          placeholder="Well-maintained, spotless and serviced on time. Perfect for city runs and weekend getaways..."
          className={`${inputClass} resize-none`}
        />
        <p className="mt-2 text-right text-xs text-foreground-500">{description.length}/500</p>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex cursor-pointer items-center justify-between rounded-xl border border-background-200 bg-background-50 px-4 py-3.5">
            <span className="flex items-center gap-2.5 text-sm text-foreground-900">
              <i className="ri-flashlight-line text-primary-500"></i> Instant book
            </span>
            <input type="checkbox" checked={instantBook} onChange={(e) => setInstantBook(e.target.checked)} className="h-4 w-4 accent-primary-500" />
          </label>
          <label className="flex cursor-pointer items-center justify-between rounded-xl border border-background-200 bg-background-50 px-4 py-3.5">
            <span className="flex items-center gap-2.5 text-sm text-foreground-900">
              <i className="ri-map-pin-2-line text-primary-500"></i> Offer delivery
            </span>
            <input type="checkbox" checked={delivery} onChange={(e) => setDelivery(e.target.checked)} className="h-4 w-4 accent-primary-500" />
          </label>
        </div>
      </Section>

      {status === "error" && formError && (
        <p className="flex items-center gap-2 rounded-lg border border-secondary-700/40 bg-secondary-950/40 px-4 py-3 text-sm text-secondary-300">
          <i className="ri-error-warning-line"></i> {formError}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-200 bg-background-100 px-5 py-3 text-sm font-medium text-foreground-900 transition-colors hover:border-primary-500/60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Publishing your car
            </>
          ) : (
            <>
              <i className="ri-check-line"></i> Publish listing
            </>
          )}
        </button>
      </div>
    </form>
  );
}